import { useCallback, useEffect, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Client, useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import {
  FeaturesConfigurationFeatureTypesForm,
  FeatureTypesConfigFormValues,
} from 'src/screens/administration/configuration-management/features-configuration/feature-types/edit/FeaturesConfigurationFeatureTypesForm'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { CreateButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { Feature_Type_Config_Insert_Input, Mutation_Root, Query_Root } from 'src/@types/graphql'
import {
  insertFeatureTypeConfig,
  queryHighestFeatureTypeSortNumber,
} from 'src/screens/administration/configuration-management/features-configuration/feature-types/featureTypesConfigQueries'

const defaultInitialValues: FeatureTypesConfigFormValues = {
  enName: '',
  deName: '',
  frName: '',
  itName: '',
  enDescription: '',
  deDescription: '',
  frDescription: '',
  itDescription: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  hasKapFactsheetValidation: false,
  helpAndInstructionsKey: '',
}

const fetchHighestFeatureTypeSortNumber = (urqlClient: Client) => async () => {
  const { data, error } = await urqlClient
    .query<{
      feature_type_config_aggregate: Query_Root['feature_type_config_aggregate']
    }>(queryHighestFeatureTypeSortNumber, {})
    .toPromise()

  if (error || !data?.feature_type_config_aggregate.aggregate?.max?.sort_number) {
    throw new Error('Resource not found')
  }

  const fetchedHighestSortNumber = data.feature_type_config_aggregate.aggregate.max.sort_number
  return { fetchedHighestSortNumber }
}

export const FeaturesConfigurationFeatureTypesCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [initialValues, setInitialValues] = useState<FeatureTypesConfigFormValues | null>(null)
  const [formDirty, setFormDirty] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureTypes.params({}))
  }, [navigate])

  const onCreate = async () => {
    document.getElementById('feature-types-config-form')?.dispatchEvent(
      new Event('submit', {
        cancelable: true,
        bubbles: true,
      }),
    )
  }

  const handleSubmitLocal = useCallback(
    async (values: FeatureTypesConfigFormValues) => {
      let newSortNumber: number
      try {
        const { fetchedHighestSortNumber } = await fetchHighestFeatureTypeSortNumber(urqlClient)()
        newSortNumber = fetchedHighestSortNumber + 1
      } catch (e) {
        notificationService.operationFailed()
        return
      }

      const featureTypeInsertInput: Feature_Type_Config_Insert_Input = {
        code: crypto.randomUUID(),
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        description: {
          de: values.deDescription,
          en: values.enDescription,
          fr: values.frDescription,
          it: values.itDescription,
        },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        has_kap_factsheet_validation: values.hasKapFactsheetValidation,
        sort_number: newSortNumber,
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_feature_type_config: Mutation_Root['insert_feature_type_config']
        }>(insertFeatureTypeConfig, {
          featureTypeInsertInput,
        })
        .toPromise()

      if (!error && data?.insert_feature_type_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, urqlClient],
  )

  return (
    <ScreenLayout
      title={getMessage('label.configuration.features.type.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <FeaturesConfigurationFeatureTypesForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              formDirty={formDirty}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
